import { NavLink } from 'react-router';
import {
  Home,
  Trophy,
  Zap,
  Star,
  Clock,
  Settings,
  ChevronRight,
  Menu,
  X,
  List,
  Calendar,
} from 'lucide-react';
import { useState } from 'react';
import { useChannelsStore } from '@/store/useChannelsStore';
import { useFavoritesStore } from '@/store/useFavoritesStore';
import { useRecentsStore } from '@/store/useRecentsStore';


const primaryNav = [
  { path: '/', label: 'Home', icon: Home, exact: true },
  { path: '/matches', label: 'Matches', icon: Calendar },
  { path: '/world-cup', label: 'World Cup', icon: Trophy },
  { path: '/top-sports', label: 'Top Sports', icon: Zap },
];

const secondaryNav = [
  { path: '/favorites', label: 'Favorites', icon: Star, countKey: 'favorites' },
  { path: '/recent', label: 'Recent', icon: Clock, countKey: 'recents' },
  { path: '/all-channels', label: 'All Channels', icon: List, countKey: null },
] as const;

export function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const countries = useChannelsStore((s) => s.countries);
  const favoriteCount = useFavoritesStore((s) => s.favoriteIds.length);
  const recentCount = useRecentsStore((s) => s.recentEntries.length);

  const getCount = (key: (typeof secondaryNav)[number]['countKey']): number | null => {
    if (key === 'favorites') return favoriteCount;
    if (key === 'recents') return recentCount;
    return null;
  };

  // Top 10 countries by channel count
  const topCountries = countries.slice(0, 10);
  const visibleCountries = showAllCountries ? countries : topCountries;

  return (
    <>
      <button
        className="lg:hidden fixed bottom-4 right-4 z-40 h-12 w-12 rounded-full bg-accent text-white shadow-lg flex items-center justify-center"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-bg-elevated border-r border-border flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        aria-label="Main navigation"
      >
        <div className="flex-1 overflow-y-auto py-4 px-3">
          <nav className="space-y-1" aria-label="Primary navigation">
            {primaryNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-accent/10 text-accent'
                      : 'text-text-muted hover:bg-bg-hover hover:text-text'
                  }`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-center justify-between px-3 py-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                Countries
              </p>
              {countries.length > 10 && (
                <button
                  onClick={() => setShowAllCountries(!showAllCountries)}
                  className="text-xs text-text-muted hover:text-text transition-colors flex items-center gap-1"
                  aria-expanded={showAllCountries}
                >
                  {showAllCountries ? 'Show less' : 'Show all'}
                  <ChevronRight
                    className={`w-3 h-3 transition-transform ${
                      showAllCountries ? 'rotate-90' : ''
                    }`}
                  />
                </button>
              )}
            </div>
            <nav className="space-y-0.5" aria-label="Browse by country">
              {visibleCountries.map((country) => (
                <NavLink
                  key={country.code}
                  to={`/country/${country.code}`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'bg-accent/10 text-accent'
                        : 'text-text-muted hover:bg-bg-hover hover:text-text'
                    }`
                  }
                >
                  <span className="text-base w-5 flex-shrink-0 text-center" aria-hidden="true">
                    {country.flag}
                  </span>
                  <span className="flex-1 truncate">{country.name}</span>
                  <span className="text-[10px] text-text-muted tabular-nums">
                    {country.count}
                  </span>
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <nav className="space-y-1" aria-label="User navigation">
              {secondaryNav.map((item) => {
                const count = getCount(item.countKey);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-accent/10 text-accent'
                          : 'text-text-muted hover:bg-bg-hover hover:text-text'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span className="flex-1">{item.label}</span>
                    {count !== null && count > 0 && (
                      <span className="text-[10px] font-semibold bg-bg-hover text-text-muted px-1.5 py-0.5 rounded-full tabular-nums">
                        {count}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="p-3 border-t border-border">
          <NavLink
            to="/settings"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-accent/10 text-accent'
                  : 'text-text-muted hover:bg-bg-hover hover:text-text'
              }`
            }
          >
            <Settings className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            Settings
          </NavLink>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}